import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, finalize, forkJoin, of, take } from 'rxjs';
import {
  IntroductionUnconfirmedItem,
  isIntroductionDocumentAgreed,
} from 'src/app/shared/models/document-to-learn.model';
import { ApiService } from 'src/app/core/services/api.service';
import { DocumentsToLearnService } from 'src/app/shared/services/documents-to-learn/documents-to-learn.service';
import { DocumentsToLearnComplianceService } from 'src/app/shared/services/documents-to-learn/documents-to-learn-compliance.service';
import { UserService } from 'src/app/core/services/user.service';
import { ModalService } from 'src/app/core/services/modal.service';
import { PaTagComponent } from 'src/app/shared/ui-kit/pa-tag/pa-tag.component';
import { CheckboxModule } from 'primeng/checkbox';
import { PaButtonComponent } from 'src/app/shared/ui-kit/pa-button/pa-button.component';
import { PaMessageComponent } from 'src/app/shared/ui-kit/pa-message/pa-message.component';
import { DocumentModalComponent } from 'src/app/shared/components/document-modal/document-modal.component';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { AnalyticsService } from 'src/app/core/services/analytics.service';

@Component({
  imports: [
    NgFor,
    FormsModule,
    PaTagComponent,
    CheckboxModule,
    PaButtonComponent,
    PaMessageComponent,
    ConfirmDialog,
  ],
  selector: 'app-introduction',
  standalone: true,
  templateUrl: './introduction-documents.component.html',
  styleUrls: ['./introduction-documents.component.scss'],
  providers: [ConfirmationService],
})
export class IntroductionDocumentsComponent implements AfterViewInit {
  @ViewChild('introductionLayout')
  private readonly introductionLayoutRef?: ElementRef<HTMLDivElement>;

  private readonly destroy = inject(DestroyRef);
  public readonly confirmationService: ConfirmationService = inject(ConfirmationService);
  public readonly modalService: ModalService = inject(ModalService);
  private readonly api = inject(ApiService);
  private readonly documentsToLearnService: DocumentsToLearnService = inject(DocumentsToLearnService);
  private readonly documentsCompliance = inject(DocumentsToLearnComplianceService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);

  messageVisible = true;
  isActionsFixed = false;
  introAgreeChecked = false;
  isDocumentsLoading = true;
  /** true — все документы подтверждены, можно продолжать */
  isAllConfirmed = false;

  documents: IntroductionUnconfirmedItem[] = [];

  readonly isIntroductionAgreed = isIntroductionDocumentAgreed;

  /**
   * true — «Продолжить» неактивна, пока по всем документам нет согласия
   * (только поштучные модалки). false — достаточно чекбокса; accept шлёт
   * POST confirm/{id} по документам без актуального согласия.
   */
  private readonly blockContinueUntilAllDocumentsAgreed = false;

  get allDocumentsAgreed(): boolean {
    if (this.documents.length === 0) {
      return true;
    }

    return this.documents.every((d) => isIntroductionDocumentAgreed(d));
  }

  get isContinueDisabled(): boolean {
    if (!this.introAgreeChecked && !this.isAllConfirmed) {
      return true;
    }

    if (
      this.blockContinueUntilAllDocumentsAgreed &&
      !this.allDocumentsAgreed
    ) {
      return true;
    }

    return false;
  }

  ngAfterViewInit(): void {
    if (this.userService.isExemptFromDocumentsToLearnCompliance()) {
      this.documentsCompliance.navigateToAppAfterIntroduction();
      return;
    }

    this.loadDocuments();
    this.updateActionsFixedState();
    setTimeout(() => this.updateActionsFixedState(), 0);
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.updateActionsFixedState();
  }

  openDocumentModal(document: IntroductionUnconfirmedItem): void {
    if (!document?.id) {
      return;
    }

    this.api.loadingPresent();

    this.documentsToLearnService
      .getDocumentsByIds(String(document.id))
      .pipe(
        takeUntilDestroyed(this.destroy),
        catchError((error) => {
          this.analyticsService.phCaputeException('Документы: Ошибка загрузки документа для ознакомления', { error });
          return of([]);
        }),
        finalize(() => {
          this.api.loadingDismiss();
        }),
      )
      .subscribe({
        next: (items) => {
          const loadedDocument = items[0];
          if (!loadedDocument) {
            return;
          }

          this.documents = this.documents.map((item) =>
            item.id === loadedDocument.id
              ? loadedDocument
              : item,
          );

          this.modalService
            .createWithDynamicComponent({
              component: DocumentModalComponent,
              componentProps: {
                data: loadedDocument,
                onConfirmed: (confirmedItem: IntroductionUnconfirmedItem) => {
                  const updatedDocuments = this.documents.map((item) =>
                    item.id === confirmedItem.id
                      ? { ...item, ...confirmedItem }
                      : item,
                  );

                  this.documents = updatedDocuments;

                  this.isAllConfirmed = updatedDocuments.every((item) =>
                    isIntroductionDocumentAgreed(item),
                  );
                },
              },
            })
            .pipe(takeUntilDestroyed(this.destroy))
            .subscribe();
        },
      });
  }

  /**
   * Загружает список неподтверждённых документов для ознакомления.
   * @param after - callback, который будет вызван после загрузки документов.
   */
  private loadDocuments(after?: () => void): void {
    this.isDocumentsLoading = true;

    this.documentsToLearnService
      .getUnconfirmedDocuments()
      .pipe(
        takeUntilDestroyed(this.destroy),
        finalize(() => {
          this.isDocumentsLoading = false;
          setTimeout(() => this.updateActionsFixedState(), 0);
        }),
      )
      .subscribe({
        next: (documents) => {
          this.documents = [...documents].sort((left, right) => {
            const leftDate = new Date(left.updated || left.added).getTime();
            const rightDate = new Date(right.updated || right.added).getTime();

            return rightDate - leftDate;
          });

          if (this.documents.length === 0) {
            this.documentsCompliance.clearIntroductionBlocked();
            this.navigateAfterIntroduction();
            return;
          }

          setTimeout(() => after?.(), 0);
        },
        error: () => {
          this.documents = [];
        },
      });
  }

  private updateActionsFixedState(): void {
    const layout = this.introductionLayoutRef?.nativeElement;
    if (!layout) {
      this.isActionsFixed = false;
      return;
    }

    this.isActionsFixed = layout.scrollHeight > layout.clientHeight + 1;
  }

  onContinue(): void {
    if (this.isContinueDisabled) {
      return;
    }

    if (this.isAllConfirmed) {
      this.navigateAfterIntroduction();
      return;
    }

    this.analyticsService.phCapture('Документы: Кнопка Продолжить нажата (отдельная страница подтверждения)');

    this.confirmationService.confirm({
      header: 'Не все обязательные документы подтверждены',
      message:
        'Продолжая, вы подтверждаете согласие со всеми обязательными '
        + 'документами. Отменить согласие будет невозможно.',
      rejectButtonProps: {
        label: 'Отмена',
        severity: 'secondary',
        variant: 'text',
        rounded: true,
      },
      acceptButtonProps: {
        label: 'Продолжить',
        severity: 'primary',
        rounded: true,
      },
      accept: () => this.handleContinueAccept(),
      reject: () => {},
      closable: false,
    });
  }

  onIntroAgreeCheckedChange(): void {
    this.analyticsService.phCapture('Документы: Чекбокс "Я согласен" нажат (отдельная страница подтверждения)');
  }

  /**
   * Обрабатывает нажатие кнопки "Продолжить".
   */
  private handleContinueAccept(): void {
    if (this.blockContinueUntilAllDocumentsAgreed) {
      if (!this.allDocumentsAgreed) {
        return;
      }
      this.navigateAfterIntroduction();
      return;
    }

    const pending = this.documents.filter((d) => !isIntroductionDocumentAgreed(d));

    if (pending.length === 0) {
      this.navigateAfterIntroduction();
      return;
    }

    void this.api.loadingPresent();

    forkJoin(
      pending.map((doc) =>
        this.documentsToLearnService.confirmDocument(doc.id).pipe(
          catchError(() => of(null)),
        ),
      ),
    )
      .pipe(
        take(1),
        finalize(() => {
          this.api.loadingDismiss();
        }),
      )
      .subscribe((results) => {
        const allSuccess = results.every(
          (res) => res && 'success' in res && res.success,
        );

        if (!allSuccess) {
          this.loadDocuments();
          return;
        }

        this.loadDocuments(() => this.navigateAfterIntroduction());
      });
  }

  private navigateAfterIntroduction(): void {
    this.documentsCompliance.navigateToAppAfterIntroduction();
  }
}